import axios from "axios";
import { store } from "@/store/share/index";
import WebStorage from "@/store/share/storage/webStorage";
import {
  Bookmark,
  Column,
  ID,
  Label,
  StorageData,
  TaskData,
} from "@/types/task";
import { defaultsBookmarks, State } from "@/store/share/state";
import {
  DEFAULT_LABELS,
  FILE_NAME,
  LOADING,
  META,
  NOTIFICATION,
  V2_DEFAULT_LABELS,
} from "@/types/constant";
import { File } from "@/types/drive";
import { MutationTypes } from "@/store/share/mutation-types";
import { LoadingDataType } from "@/store/share/mutations";
import { convertNumberToEnglish } from "@/utils/share";

type StorageType = "webStorage" | "chromeStorage";

export default class Storage<T = object> {
  private readonly storageType: StorageType;

  public web;

  constructor(storageType: StorageType = process.env.VUE_APP_STORAGE_NAME) {
    this.storageType = storageType;

    this.web = new WebStorage();
  }

  public state(version = 2): object {
    if (version === 1) {
      const data = {
        COLUMNS: [],
        CHECK_LIST: [],
        SETTINGS: {},
      };

      return data;
    } else if (version === 2) {
      const {
        googleToken,
        userInfo,
        settings,
        bookmarks,
        columns,
        labels,
        checkList,
      } = store.state;

      const data = {
        meta: META,
        googleToken,
        userInfo,
        settings,
        bookmarks,
        columns,
        labels,
        checkList,
      };

      return data;
    }

    return {};
  }

  public get(): State | null {
    return this.web.get();
  }

  public dataVersion(data: any): object | null {
    const isVersionTwo = data?.meta?.version === 2;
    const stateData: object | any = isVersionTwo
      ? this.state(2)
      : this.state(1);
    for (const stateDataKey in stateData) {
      if (!data[stateDataKey]) {
        store.commit(MutationTypes.SET_NOTIFICATION, {
          text: NOTIFICATION.fileSelectedFormat,
          type: "error",
          name: "fileSelectedFormat",
          time: 4000,
        });

        return null;
      }
    }

    let finalData = {};
    if (isVersionTwo) {
      const { settings, bookmarks, columns, labels, checkList } = data;
      finalData = {
        meta: META,
        settings,
        bookmarks,
        columns,
        labels,
        checkList,
        userInfo: store.state.userInfo,
        googleToken: store.state.googleToken,
      };
    } else {
      let customColumns = [];
      const customLabels = [];

      customColumns = data.COLUMNS.map((column: object | any) => {
        return {
          ...column,
          tasks: column.tasks.map((task: object | any) => ({
            ...task,
            label: task.priority,
            comments: task?.comments || [],
          })),
        };
      });

      if (data.LABELS) {
        for (const labelsKey in data.LABELS) {
          customLabels.push({
            id: data.LABELS[labelsKey].name,
            text: data.LABELS[labelsKey].title,
            color: data.LABELS[labelsKey].color,
          });
        }
      }

      finalData = {
        meta: META,
        settings: {},
        bookmarks: defaultsBookmarks(true),
        columns: customColumns,
        checkList: data.CHECK_LIST,
        labels: customLabels.length ? customLabels : V2_DEFAULT_LABELS,
        userInfo: store.state.userInfo,
        googleToken: store.state.googleToken,
      };
    }

    return finalData;
  }

  public async getFromDrive(): Promise<void> {
    const state = await this.web.getFromDrive();
    const finalData = this.dataVersion(state);
    if (!finalData) return;
    store.commit(MutationTypes.SET_STATE, finalData);
  }

  public async mergeFromDrive(): Promise<void | null> {
    const state: any = await this.web.getFromDrive();
    const driveState: any = this.dataVersion(state);

    // Merge Column
    const storeColumn = store.state.columns;
    const driveColumn = [...driveState.columns];

    driveColumn.forEach((driveItem: Column) => {
      const findStoreColumn = storeColumn.find(
        (storeItem: Column) => storeItem.id === driveItem.id
      );
      if (findStoreColumn) {
        const tasks = [...findStoreColumn.tasks];
        driveItem.tasks.forEach((driveTask: TaskData) => {
          const isDriveTaskDuplicate = tasks.some(
            (task) => task.id === driveTask.id
          );
          if (!isDriveTaskDuplicate) {
            tasks.push(driveTask);
          }
        });
        findStoreColumn.tasks = tasks;
      } else {
        throw new Error("==== COLUMN NOT FOUND ====");
        // TODO: push dynamic columns
        // storeColumn.push(driveItem);
      }
    });
    driveState.columns = storeColumn;

    // Merge Checklist
    let storeCheckList = store.state.checkList;
    const driveCheckList = driveState?.checkList.map((driveItem: any) => {
      if (
        !storeCheckList.some((storeItem) => storeItem?.id === driveItem?.id)
      ) {
        return driveItem;
      }
    });

    storeCheckList = [...storeCheckList, ...driveCheckList];

    driveState.checkList = storeCheckList;

    // Merge labels
    const storeLabels = store.state.labels;
    const driveLabels = driveState.labels;

    driveLabels.forEach((label: Label) => {
      if (!storeLabels.some((storeLabel) => storeLabel.id === label.id)) {
        storeLabels.push(label);
      }
    });
    driveState.labels = storeLabels;

    // Merge Bookmarks
    const storeBookmarks = store.state.bookmarks.filter(
      (bookmark: Bookmark | null) => bookmark
    );
    const driveBookmark = driveState.bookmarks.filter(
      (bookmark: Bookmark | null) =>
        bookmark &&
        !storeBookmarks.some(
          (storeBookmark: Bookmark | null) =>
            storeBookmark?.url === bookmark.url
        )
    );
    // Merge local and drive bookmarks
    const mergedBookmark = [...storeBookmarks, ...driveBookmark];
    // Create empty bookmark for display the least items
    const nullArray = new Array(
      defaultsBookmarks().length - mergedBookmark.length > 0
        ? defaultsBookmarks().length - mergedBookmark.length
        : 1
    ).fill(null);
    // Clone mergedBookmark and empty bookmarks
    driveState.bookmarks = [...mergedBookmark, ...nullArray];

    store.commit(MutationTypes.SET_STATE, driveState);
  }

  public async set(loading?: LoadingDataType): Promise<void> {
    if (loading) {
      store.commit(MutationTypes.SET_LOADING, loading);
    } else {
      store.commit(MutationTypes.SET_LOADING, {
        text: LOADING.saving,
        isOpen: true,
      });
    }

    await this.web.set(this.state());
    store.commit(MutationTypes.SET_LOADING, { isOpen: false });
  }

  public setImage(): void {
    const data = store.state;
    this.web.setImage(data);
  }

  public createStateFile(): void {
    this.web.createStateFile(this.state());
  }

  public async importStateFile(file: Blob): Promise<void> {
    const webSet = async (data: object | any) => {
      const finalData = this.dataVersion(data);
      if (!finalData) return;

      store.commit(MutationTypes.SET_LOADING, {
        text: LOADING.saving,
        isOpen: true,
      });
      // Set file data into Google Drive
      await this.web.set(finalData);
      store.commit(MutationTypes.SET_LOADING, { isOpen: false });
      // Set file data into local state
      store.commit(MutationTypes.SET_STATE, finalData);
    };

    const fr: any = new FileReader();
    fr.onload = function () {
      let fileData;
      try {
        fileData = JSON.parse(fr.result);
      } catch (e) {
        store.commit(MutationTypes.SET_NOTIFICATION, {
          text: NOTIFICATION.fileSelectedFormat,
          type: "error",
          name: "fileSelectedFormat",
          time: 4000,
        });
        return;
      }

      webSet(fileData);
    };

    fr.readAsText(file);
  }

  public setOnLocalStorage(): void {
    const data = store.state;
    this.web.setOnLocalStorage(data);
  }

  public mergeData(): void {
    const data = store.state;
    this.web.setOnLocalStorage(data);
  }
  // private;
}
